import { Box, Button, Dialog, DialogActions, DialogTitle, DialogContent, IconButton, DialogProps } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { styles } from './CreateFolderDialog.styles';
import { useFoldersContext } from '../../contexts/useFolders.context';
import { LocationSelect } from '../LocationSelect/LocationSelect';
import { useParentFolder } from '../LocationSelect/hooks/useParentFolder';
import { FolderTitleInput, intl as folderTitleInputIntl } from '../FolderTitleInput/FolderTitleInput';
import { useFolderTitle } from '../FolderTitleInput/hooks/useFolderTitle';
import { FolderPayload } from '../../types';

const intl = {
  title: 'New Folder',
  cancel: 'Cancel',
  create: 'Create',
  closeIconAriaLabel: 'close button for dialog'
};

type CreateFolderDialogProps = DialogProps & {
  title?: string;
  isCreating?: boolean;
  onClose: () => void;
  onCreate: (folderPayload: FolderPayload) => Promise<void>;
};

export function CreateFolderDialog({
  title = intl.title,
  isCreating = false,
  onCreate,
  ...dialogProps
}: CreateFolderDialogProps) {
  const { rootFolder } = useFoldersContext();
  const { parentFolder, subfolders, setParentFolderId } = useParentFolder();
  const { folderTitle, setFolderTitle, trimmedFolderTitle, isCreateDisabled, setError, error, helperTextWithIcon } =
    useFolderTitle({
      isCreatingFolder: isCreating,
      folders: subfolders
    });

  const handleFolderTitleChange = (value: string) => {
    setError(null);
    setFolderTitle(value);
  };

  const handleClose = () => {
    dialogProps.onClose();
    setFolderTitle(folderTitleInputIntl.defaultTitle);
    setParentFolderId(rootFolder.id);
  };

  const handleCreate = async () => {
    setError(null);

    try {
      await onCreate({ title: trimmedFolderTitle, parentFolderId: parentFolder.id });
      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      }
    }
  };

  return (
    <Dialog fullWidth {...dialogProps}>
      <Box sx={styles.header}>
        <DialogTitle>{title}</DialogTitle>
      <IconButton aria-label={intl.closeIconAriaLabel} onClick={dialogProps.onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={styles.content}>
        <FolderTitleInput
          withLabel={true}
          disabled={isCreating}
          title={folderTitle}
          error={error}
          helperText={helperTextWithIcon}
          onChange={handleFolderTitleChange}
          onPressEnter={() => {
            if (!isCreateDisabled) {
              void handleCreate();
            }
          }}
        />
        <LocationSelect selectedFolder={parentFolder} subfolders={subfolders} onFolderSelect={setParentFolderId} />
      </DialogContent>

      <DialogActions>
        <Button
          disabled={isCreating}
          color="secondary"
          data-test="cancelCreateButton"
          onClick={() => {
            handleClose();
          }}
        >
          {intl.cancel}
        </Button>
        <LoadingButton
          loading={isCreating}
          disabled={isCreateDisabled}
          color="primary"
          variant="contained"
          data-test="createFolderButton"
          onClick={() => {
            void handleCreate();
          }}
        >
          {intl.create}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
